/**
 * The compact layout's panel sheet: the dock's panels as tabs, in a sheet
 * that rises from the bottom over a live viewport (portrait) or docks to the
 * right as a side panel (landscape). See docs/planning/07-mobile-editor.md.
 *
 * In portrait the sheet's handle (grabber and tab strip) drags it between its
 * stops, snapping on release (sheetModel). A press that doesn't move is left
 * alone, so a tap on a tab stays a tap. In landscape it opens and closes.
 *
 * Every tab's panel stays mounted, hidden when another is showing, as the
 * dock's panels keep their state while in the background.
 */
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useEntity, useSelection } from "../../hooks/editorHooks";
import { ChevronRightIcon } from "../../ui/icons";
import type { PanelId } from "../layout";
import { PANELS } from "../panels";
import { PEEK_HEIGHT, sheetHeight, sidePanelWidth, snapStop } from "./sheetModel";
import type { SheetStop } from "./sheetModel";

export type Orientation = "portrait" | "landscape";

/** Moving less than this (CSS px) is a tap on the handle, not a drag. */
const DRAG_SLOP_PX = 6;

export interface PanelSheetProps {
  readonly orientation: Orientation;
  /** Room for the sheet: the height below the top bar (portrait) or the width (landscape). */
  readonly available: number;
  readonly stop: SheetStop;
  readonly tab: PanelId;
  readonly tabs: readonly PanelId[];
  readonly onStop: (stop: SheetStop) => void;
  /** A tab was chosen (a peeking sheet also opens). */
  readonly onTab: (tab: PanelId) => void;
}

/** The sheet's size at a stop: its height in portrait, its width in landscape. */
export function sheetSize(orientation: Orientation, stop: SheetStop, available: number): number {
  return orientation === "portrait"
    ? sheetHeight(stop, available)
    : sidePanelWidth(stop, available);
}

export function PanelSheet(props: PanelSheetProps) {
  const { orientation, available, stop, tab, tabs, onStop, onTab } = props;
  const drag = useSheetDrag(props);
  const size = drag.height ?? sheetSize(orientation, stop, available);
  const style = { "--kn-sheet-size": `${size}px` } as CSSProperties;

  return (
    <>
      {orientation === "landscape" && stop === "peek" && (
        <button
          type="button"
          className="kn-sheet__open"
          onClick={() => {
            onStop("half");
          }}
        >
          Panels
        </button>
      )}
      <section
        className="kn-sheet"
        aria-label="Panels"
        data-stop={stop}
        data-orientation={orientation}
        data-dragging={drag.height !== null}
        style={style}
      >
        <div className="kn-sheet__handle" {...drag.handlers}>
          <div className="kn-sheet__grab-row">
            <SelectionChip
              onInspect={() => {
                onTab("inspector");
              }}
            />
            <span className="kn-sheet__grabber" aria-hidden="true" />
            {orientation === "landscape" && (
              <button
                type="button"
                className="kn-sheet__close"
                aria-label="Close panels"
                onClick={() => {
                  onStop("peek");
                }}
              >
                <ChevronRightIcon size={14} />
              </button>
            )}
          </div>
          <div className="kn-sheet__tabs" role="tablist" aria-label="Panels">
            {tabs.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`kn-sheet-tab-${id}`}
                aria-selected={id === tab}
                aria-controls={`kn-sheet-panel-${id}`}
                className="kn-sheet__tab"
                onClick={() => {
                  onTab(id);
                }}
              >
                {PANELS[id].title}
              </button>
            ))}
          </div>
        </div>
        <div className="kn-sheet__body">
          {tabs.map((id) => {
            const { Component } = PANELS[id];
            return (
              <div
                key={id}
                role="tabpanel"
                id={`kn-sheet-panel-${id}`}
                aria-labelledby={`kn-sheet-tab-${id}`}
                className="kn-sheet__panel"
                hidden={id !== tab}
              >
                <Component />
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

/**
 * The selected entity's name, in the grabber row: tapping it opens the
 * Inspector on it, so a tap in the viewport is one more tap from its details.
 */
function SelectionChip({ onInspect }: { onInspect: () => void }) {
  const { active } = useSelection();
  const entity = useEntity(active);
  if (!entity) return <span className="kn-sheet__chip-space" />;

  return (
    <button
      type="button"
      className="kn-sheet__chip"
      aria-label={`Inspect ${entity.name}`}
      onClick={onInspect}
    >
      <span className="kn-sheet__chip-name">{entity.name}</span>
      <ChevronRightIcon size={12} />
    </button>
  );
}

interface DragState {
  readonly startY: number;
  readonly startHeight: number;
  lastY: number;
  lastTime: number;
  /** px/ms, positive while the sheet grows. */
  velocity: number;
  moved: boolean;
}

/**
 * Dragging the portrait sheet by its handle. The height follows the finger
 * (between peek and the room available); on release it settles on a stop.
 *
 * The drag is followed on the window rather than by capturing the pointer:
 * a captured pointer's click would land on the handle, not the tab under it,
 * so taps on tabs would stop working. A press that moves less than the slop
 * is left alone as a tap; one that drags swallows the click that follows.
 */
function useSheetDrag({ orientation, available, stop, onStop }: PanelSheetProps) {
  const detach = useRef<(() => void) | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(
    () => () => {
      detach.current?.();
    },
    [],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (orientation !== "portrait" || detach.current) return;
    const state: DragState = {
      startY: event.clientY,
      startHeight: sheetHeight(stop, available),
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      moved: false,
    };
    const pointerId = event.pointerId;

    const onMove = (move: PointerEvent) => {
      if (move.pointerId !== pointerId) return;
      if (!state.moved && Math.abs(move.clientY - state.startY) < DRAG_SLOP_PX) return;
      state.moved = true;

      const elapsed = move.timeStamp - state.lastTime;
      if (elapsed > 0) state.velocity = (state.lastY - move.clientY) / elapsed;
      state.lastY = move.clientY;
      state.lastTime = move.timeStamp;
      setHeight(heightAt(state, move.clientY, available));
    };
    const onEnd = (up: PointerEvent) => {
      if (up.pointerId !== pointerId) return;
      detach.current?.();
      if (!state.moved) return;

      swallowNextClick();
      setHeight(null);
      const settled = up.type === "pointerup";
      const released = heightAt(state, up.clientY, available);
      onStop(settled ? snapStop(released, state.velocity, available) : stop);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    detach.current = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      detach.current = null;
    };
  };

  return { height, handlers: { onPointerDown } };
}

/** After a drag, the click the release produces isn't a tap on whatever it ended over. */
function swallowNextClick(): void {
  const swallow = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };
  window.addEventListener("click", swallow, { capture: true, once: true });
  // A release that produces no click (off the page, or cancelled) mustn't eat a later tap.
  setTimeout(() => {
    window.removeEventListener("click", swallow, { capture: true });
  }, 0);
}

/** The sheet's height with the finger at `clientY`: dragging up grows it. */
function heightAt(state: DragState, clientY: number, available: number): number {
  const height = state.startHeight + (state.startY - clientY);
  return Math.max(PEEK_HEIGHT, Math.min(available, height));
}
